import React, { useState, memo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

import marketReportCard from "../../assets/images/marketreportCard_V2.png";
import InquiryModal from "../../common/InquiryModal";
import PurchaseModal from "../../common/PurchaseModal";
import {
  FaEnvelope,
  FaChartLine,
  FaRegCalendarAlt,
  FaMapMarkerAlt,
  FaRegStar,
  FaPencilAlt,
} from "react-icons/fa";
import { IoCartOutline } from "react-icons/io5";

const ReportCard = ({ report }) => {
  const { endUser } = useAuth() || {};
  const [showModal, setShowModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [modalType, setModalType] = useState("sample_download");

  const {
    title: rawTitle,
    cagr = report.cagr || report.CAGR || "XX%",
    forecastPeriod = report.forecastPeriod ||
      report.forecast_period ||
      report.ForecastPeriod,
    study_p = report.StudyPeriod || report.studyPeriod || forecastPeriod,
    studyPeriod = study_p && study_p !== "N/A" ? study_p : "2021 - 2031F",
    regionBase = report.region || report.RegionsCovered || "Global",
    country = report.country,
    region = country && regionBase !== "Global"
      ? `${regionBase} (${country})`
      : regionBase,
    majorPlayers = report.majorPlayers ||
      report.major_players ||
      report.MajorPlayers,
    players = (() => {
      if (!majorPlayers || majorPlayers === "Key Players...")
        return "Key Players (Top 10)...";
      if (Array.isArray(majorPlayers)) return majorPlayers.join(", ");
      if (typeof majorPlayers === "string") return majorPlayers;
      return "Key Players (Top 10)...";
    })(),
    category = report.category || report.Industry,
  } = report;

  const title = rawTitle ? rawTitle.replace(/\s*-\s*$/, "") : "";
  const navigate = useNavigate();

  const isUserLoggedIn = Boolean(
    (endUser && (endUser.email || endUser.id)) ||
    (() => {
      try {
        const u = JSON.parse(localStorage.getItem("rw_end_user") || "null");
        return u && (u.email || u.id);
      } catch (e) {
        return false;
      }
    })()
  );

  const handleReportClick = () => {};

  const handleOpenModal = (type) => {
    setModalType(type);
    setShowModal(true);
  };

  const handleSampleClick = () => {
    if (!isUserLoggedIn) {
      const redirectUrl = `/reports/${report.slug || report.id}`;
      navigate(`/signin?redirect=${encodeURIComponent(redirectUrl)}`);
      return;
    }
    handleOpenModal("sample_download");
  };

  const handleBuyNow = () => {
    const targetUrl = `/purchase/${report.slug || report.id}`;
    if (!isUserLoggedIn) {
      navigate(`/signin?redirect=${encodeURIComponent(targetUrl)}`);
      return;
    }
    navigate(targetUrl, { state: { report } });
  };

  const handleCustomize = () => {
    const targetUrl = "/request-custom-research";
    if (!isUserLoggedIn) {
      navigate(`/signin?redirect=${encodeURIComponent(targetUrl)}`);
      return;
    }
    navigate(targetUrl, { state: { report } });
  };

  return (
    <div className="row report-card-container rounded m-1 mb-2 bg-white align-items-center">
      {/* Left side: Report Cover Image */}
      <div className="col-lg-2 col-3 d-flex align-items-center justify-content-center p-2">
        <Link
          to={`/reports/${report.slug || report.id}`}
          state={{ report }}
          className="card-book-link d-inline-block text-decoration-none w-100"
        >
          <div className="card-book-wrapper">
            <img
              src={marketReportCard}
              alt={`${title || "Market"} Report Cover`}
              className="card-book-img img-fluid rounded"
              loading="lazy"
              decoding="async"
            />
            <div className="card-book-overlay position-absolute">
              <p className="card-book-title mb-0 fw-bold" title={title}>
                {title}
              </p>
            </div>
          </div>
        </Link>
      </div>

      {/* Middle: Report Details */}
      <div className="col-lg-7 col-9 py-2 px-3">
        <div className="d-flex justify-content-between align-items-start mb-1">
          <h3
            className="h6 fw-bold text-dark mb-0 pe-2"
            style={{ lineHeight: "1.3" }}
          >
            <Link
              to={`/reports/${report.slug || report.id}`}
              state={{ report }}
              onClick={handleReportClick}
              className="text-decoration-none text-dark report-title-link"
            >
              {title || category || "Market Report"}
            </Link>
          </h3>
        </div>

        <div
          className="d-flex flex-column card-text mt-2 mb-1"
          style={{ gap: "2px" }}
        >
          <div className="d-flex flex-wrap" style={{ gap: "2px 16px" }}>
            <div className="d-flex align-items-center text-muted">
              <FaChartLine
                className="me-2"
                size={14}
                style={{ color: "#8a929a" }}
              />
              <span className="fw-bold me-1">CAGR:</span> {cagr}
            </div>
            <div className="d-flex align-items-center text-muted">
              <FaRegCalendarAlt
                className="me-2"
                size={14}
                style={{ color: "#8a929a" }}
              />
              <span className="fw-bold me-1">Forecast Period:</span>{" "}
              {studyPeriod}
            </div>
          </div>

          <div className="d-flex align-items-center text-muted mt-1">
            <FaMapMarkerAlt
              className="me-2"
              size={14}
              style={{ color: "#8a929a" }}
            />
            <span className="fw-bold me-1">Region:</span> {region}
          </div>

          <div className="d-flex align-items-center text-muted mt-1 pe-2 overflow-hidden w-100">
            <FaRegStar
              className="me-2 flex-shrink-0"
              size={14}
              style={{ color: "#8a929a" }}
            />
            <div className="text-truncate w-100" title={players}>
              <span className="fw-bold me-1">Major Players:</span>
              {players}
            </div>
          </div>
        </div>

        <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center small gap-1 gap-lg-0 pt-1"></div>
      </div>
      <div className="col-lg-3 col-12 d-flex align-items-center justify-content-center px-2 py-1 py-lg-2 border-lg-start mb-1 overflow-hidden">
        <div
          className="d-flex flex-row flex-lg-column justify-content-between w-100 px-0 px-lg-3 py-1"
          style={{ gap: "8px" }}
        >
          <button
            className="btn flex-fill d-flex align-items-center justify-content-center gap-1 gap-lg-2 text-nowrap transition rounded-pill border-0 report-action-btn px-1 py-2"
            style={{ color: "#053b84", fontSize: "0.8rem" }}
            onClick={handleSampleClick}
            aria-label={`Download Sample for ${title}`}
          >
            <FaEnvelope size={12} style={{ color: "#053b84" }} />{" "}
            <span className="fw-medium">Sample</span>
          </button>
          <button
            className="btn flex-fill d-flex align-items-center justify-content-center gap-1 gap-lg-2 text-nowrap transition rounded-pill border-0 report-action-btn px-1 py-2"
            style={{ color: "#053b84", fontSize: "0.8rem" }}
            onClick={handleCustomize}
            aria-label={`Request Customization for ${title}`}
          >
            <FaPencilAlt size={12} style={{ color: "#053b84" }} />{" "}
            <span className="fw-medium">Customize</span>
          </button>
          <button
            className="btn flex-fill d-flex align-items-center justify-content-center gap-1 gap-lg-2 text-nowrap transition rounded-pill border-0 report-action-btn px-1 py-2"
            style={{ color: "#053b84", fontSize: "0.8rem" }}
            onClick={handleBuyNow}
            aria-label={`Buy ${title} Report Now`}
          >
            <IoCartOutline size={12} style={{ color: "#053b84" }} />{" "}
            <span className="fw-medium">Buy Now</span>
          </button>
        </div>
      </div>

      {showModal && (
        <InquiryModal
          show={showModal}
          onClose={() => setShowModal(false)}
          report={report}
          type={modalType}
        />
      )}

      {showPurchaseModal && (
        <PurchaseModal
          show={showPurchaseModal}
          onClose={() => setShowPurchaseModal(false)}
          report={report}
        />
      )}
    </div>
  );
};

export default memo(ReportCard);
